import React, { useState } from "react";
import {
  Typography, Table, Button, Modal, message, Spin, Card, Row, Col, Popconfirm, Form, Tooltip, Input
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, SlidersOutlined, SunOutlined } from "@ant-design/icons";
import { apiFetch } from "../utils/api";
import useEmissionPoints from '../hooks/useEmissionPoints';
import useDevices from "../hooks/useDevices";
import useTableSearchPagination from "../hooks/useTableSearchPagination";
import EmissionPointModal from "../components/EmissionPointModal";
// import EmissionRateModal from "../components/EmissionRateModal";
// import SolarRadiationModal from "../components/SolarRadiationModal";

const { Title, Paragraph } = Typography;
const { confirm: ConfirmModal } = Modal;

export default function EmissionPointsView() {
  const { emissionPoints, loading, error, reload } = useEmissionPoints();
  const { devicesWithAnemometer } = useDevices();
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("create");
  const [selectedEmissionPoint, setSelectedEmissionPoint] = useState(null);
  const [saving, setSaving] = useState(false);
  // const [editingRatesForId, setEditingRatesForId] = useState(null);
  // const [editingRadiationForId, setEditingRadiationForId] = useState(null);

  const openCreateModal = () => {
    setMode("create");
    setSelectedEmissionPoint(null);
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setMode("edit");
    setSelectedEmissionPoint(record);
    setModalOpen(true);
  };

  const handleSave = async (values, form) => {
    setSaving(true);
    try {
      const url = mode === "create" ? "/api/emission-points" : `/api/emission-points/${selectedEmissionPoint.id}`;
      const method = mode === "create" ? "POST" : "PUT";

      const res = await apiFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      message.success(mode === "create" ? "Emission point created" : "Emission point updated");
      form.resetFields();
      setModalOpen(false);
      reload();
    } catch (err) {
      message.error("Failed to save emission point");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    ConfirmModal({
      title: "Are you sure you want to delete this emission point?",
      icon: <ExclamationCircleOutlined />,
      content: `${record.name} will be permanently removed.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const res = await apiFetch(`/api/emission-points/${record.id}`, { method: "DELETE" });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          message.success("Emission point deleted");
          reload();
        } catch (err) {
          message.error("Failed to delete emission point");
          console.error(err);
        }
      },
    });
  };

  const handleCloseRates = () => setEditingRatesForId(null);
  const handleSavedRates = () => {
    setEditingRatesForId(null);
    reload(); // solo se recarga al guardar
  };

  const handleCloseSolarRad = () => setEditingRadiationForId(null);
  const handleSavedSolarRad = () => {
    setEditingRadiationForId(null);
    reload(); // solo se recarga al guardar
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Anemometer Device",
      dataIndex: "anemometerDeviceId",
      key: "anemometerDeviceId",
      render: (deviceId) => {
        const device = devicesWithAnemometer.find(d => d.id === deviceId);
        return device ? device.name : "-";
      }
    },
    { title: "Latitude (º)", dataIndex: "latitude", key: "latitude" },
    { title: "Longitude (º)", dataIndex: "longitude", key: "longitude" },
    { title: "Height (m)", dataIndex: "height", key: "height" },
    { title: "Concentration Height (m)", dataIndex: "concentrationHeight", key: "concentrationHeight" },
    // { title: "Min ppm", dataIndex: "minPpm", key: "minPpm" },
    { title: "Wind threshold (mph)", dataIndex: "windThreshold", key: "windThreshold" },
    // {
    //   title: "Emission rates",
    //   key: "emissionRates",
    //   render: (_, record) => (
    //     <>
    //       <Tooltip title="Edit">
    //         <Button
    //           icon={<SlidersOutlined />}
    //           size="small"
    //           onClick={() => setEditingRatesForId(record.id)}
    //           style={{ marginRight: 8 }}
    //         />
    //       </Tooltip>
    //     </>
    //   ),
    // },
    // {
    //   title: "Solar radiation",
    //   key: "solarradiation",
    //   render: (_, record) => (
    //     <>
    //       <Tooltip title="Edit">
    //         <Button
    //           icon={<SunOutlined />}
    //           size="small"
    //           onClick={() => setEditingRadiationForId(record.id)}
    //           style={{ marginRight: 8 }}
    //         />
    //       </Tooltip>
    //     </>
    //   ),
    // },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => openEditModal(record)}
              style={{ marginRight: 8 }}
            />
          </Tooltip>

          <Popconfirm
            title="Delete emission point?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button icon={<DeleteOutlined />} size="small" danger style={{ marginRight: 8 }} />
            </Tooltip>
          </Popconfirm>
        </>
      ),
    },
  ];

  const filterEmissionPoint = (ep, searchText) => {
    const q = searchText.toLowerCase();

    const deviceName =
      devicesWithAnemometer.find(d => d.id === ep.anemometerDeviceId)?.name || "";

    return (
      (ep.name || "").toLowerCase().includes(q) ||
      deviceName.toLowerCase().includes(q) ||
      String(ep.latitude).includes(q) ||
      String(ep.longitude).includes(q) ||
      String(ep.height).includes(q) ||
      String(ep.concentrationHeight).includes(q)
    );
  };

  const {
    searchText,
    onSearchChange,
    filteredData: filteredEmissionPoints,
    pagination
  } = useTableSearchPagination({
    data: emissionPoints,
    filterFn: filterEmissionPoint
  });

  return (
    <div style={{ padding: 24 }}>
      <Row style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Emission Points</Title>
        </Col>
      </Row>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Input
            placeholder="Search emission points..."
            value={searchText}
            onChange={e => onSearchChange(e.target.value)}
            allowClear
            style={{ width: 300 }}
          />
        </Col>

        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            style={{ backgroundColor: "#0075C9" }}
          >
            Create Emission Point
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Paragraph style={{ marginTop: 40, color: 'red' }}>{error}</Paragraph>
      ) : !emissionPoints.length ? (
        <Paragraph style={{ marginTop: 40 }}>No emission points found.</Paragraph>
      ) : (
        <Card>
          <Table
            rowKey="id"
            dataSource={filteredEmissionPoints}
            columns={columns}
            size="middle"
            pagination={pagination}
          />
        </Card>
      )}

      <EmissionPointModal
        open={modalOpen}
        mode={mode}
        form={form}
        selectedEmissionPoint={selectedEmissionPoint}
        saving={saving}
        onCancel={() => setModalOpen(false)}
        onSave={handleSave}
      />

      {/* <EmissionRateModal
        open={editingRatesForId != null}
        emissionPointId={editingRatesForId}
        onClose={handleCloseRates}
        onSaved={handleSavedRates}
      />

      <SolarRadiationModal
        open={editingRadiationForId != null}
        emissionPointId={editingRadiationForId}
        onClose={handleCloseSolarRad}
        onSaved={handleSavedSolarRad}
      /> */}
    </div>
  );
}
